import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

const SeaCargoArrivalNoticePrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (companyData) { setCompany(companyData); setLoading(false); return; }
      try {
        const docketRes = await fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuthHeaders() });
        if (docketRes.ok) {
          const docketData = await docketRes.json();
          const companyRes = await fetch(`${backendUrl}/api/companies/${docketData.company_id}`, { headers: getAuthHeaders() });
          if (companyRes.ok) setCompany(await companyRes.json());
        }
      } catch (e) {
        console.error('Failed to fetch company details:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyDetails();
  }, [docketId, companyData, backendUrl]);

  useImperativeHandle(ref, () => ({ print: () => window.print() }));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const companyName    = company?.name || 'COMPANY NAME';
  const companyLogo    = company?.logo_url;
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone   = company?.phone   || '';
  const companyEmail   = company?.email   || '';
  const companyWebsite = company?.website || '';
  const companyGst     = company?.gst_number || '';
  const companyCin     = company?.cin_number || '';

  return (
    <>
      <div
        id="sea-can-print-content"
        className="bg-white print:bg-white"
        style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.3' }}
      >
        <div className="p-6 print:p-6 relative">

          {/* Watermark */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
            <div
              className="text-gray-100 font-bold text-center whitespace-nowrap transform -rotate-45"
              style={{ fontSize: '60px', opacity: 0.08, letterSpacing: '10px' }}
            >
              {companyName}
            </div>
          </div>

          <div className="relative" style={{ zIndex: 1 }}>

            {/* Header — logo left, services right (identical to AIR CAN) */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-shrink-0">
                {companyLogo ? (
                  <img src={companyLogo} alt={companyName} className="h-16 w-auto object-contain" />
                ) : (
                  <div className="h-16 w-20 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">LOGO</div>
                )}
              </div>
              <div className="text-right text-xs">
                <p className="font-semibold">Freight Forwarder</p>
                <p className="font-semibold">Custom Broker</p>
                <p className="font-semibold">Reefer Transportation</p>
                <p className="font-semibold">Logistics Solution</p>
                {companyWebsite && <p className="mt-1 text-blue-600">{companyWebsite}</p>}
              </div>
            </div>

            {/* Horizontal rule */}
            <div className="border-t-2 border-black mb-3"></div>

            {/* Document title */}
            <div className="text-center mb-3">
              <h1 className="text-lg font-bold underline">SEA CARGO ARRIVAL NOTICE</h1>
            </div>

            {/* Job No + Date */}
            <div className="flex justify-between mb-3 text-xs">
              <div>
                <span className="font-semibold">Job No: </span>
                <span>{formData.job_no || ''}</span>
              </div>
              <div>
                <span className="font-semibold">Date: </span>
                <span>{formatDate(formData.date)}</span>
              </div>
            </div>

            {/* Main table — same structure as AIR CAN, SEA field labels */}
            <table className="w-full border-collapse border border-black text-xs mb-4">
              <tbody>

                {/* Row 1: Shipper | Consignee | Notify */}
                <tr>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Shipper</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.shipper || ''}</div>
                  </td>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Consignee</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.consignee || ''}</div>
                  </td>
                  <td className="border border-black p-2 w-1/3 align-top">
                    <div className="font-semibold mb-1">Notify Party</div>
                    <div className="whitespace-pre-wrap text-[10px]">{formData.notify || ''}</div>
                  </td>
                </tr>

                {/* Row 2: Vessel/Voyage + Date | Port of Load | Port of Discharge */}
                <tr>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Vessel / Voyage: </span>
                    <span>{formData.vessel_voyage || ''}{formData.vessel_date ? ` / ${formatDate(formData.vessel_date)}` : ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Port of Load: </span>
                    <span>{formData.port_of_load || ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Port of Discharge: </span>
                    <span>{formData.port_of_discharge || ''}</span>
                  </td>
                </tr>

                {/* Row 3: MBL No/Date | HBL No/Date | Container No / Size */}
                <tr>
                  <td className="border border-black p-2">
                    <span className="font-semibold">MBL No / Date: </span>
                    <span>{formData.mbl_no || ''}{formData.mbl_date ? ` / ${formatDate(formData.mbl_date)}` : ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">HBL No / Date: </span>
                    <span>{formData.hbl_no || ''}{formData.hbl_date ? ` / ${formatDate(formData.hbl_date)}` : ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Container No: </span>
                    <span>{formData.container_no || ''}</span>
                    {formData.size && <span className="ml-2 font-semibold">Size: <span className="font-normal">{formData.size}</span></span>}
                  </td>
                </tr>

                {/* Row 4: Pkgs HBL + GW HBL | CBM | Description */}
                <tr>
                  <td className="border border-black p-2">
                    <div>
                      <span className="font-semibold">No. of Packages (HBL): </span>
                      <span>{formData.no_of_packages_hbl || ''}</span>
                    </div>
                    <div className="mt-1">
                      <span className="font-semibold">Gross Weight (HBL): </span>
                      <span>{formData.gross_weight_hbl || ''}{formData.gross_weight_hbl ? ' KG' : ''}</span>
                    </div>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">CBM: </span>
                    <span>{formData.cbm || ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Description: </span>
                    <span>{formData.description || ''}</span>
                  </td>
                </tr>

                {/* Row 5: IGM No | IGM Date | Temperature */}
                <tr>
                  <td className="border border-black p-2">
                    <span className="font-semibold">IGM No: </span>
                    <span>{formData.igm_no || ''}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">IGM Date: </span>
                    <span>{formatDate(formData.igm_date)}</span>
                  </td>
                  <td className="border border-black p-2">
                    <span className="font-semibold">Temperature: </span>
                    <span>{formData.temperature || ''}</span>
                  </td>
                </tr>

              </tbody>
            </table>

            {/* Signature — identical to AIR CAN */}
            <div className="flex justify-end mb-4">
              <div className="text-right text-xs">
                <p className="font-semibold mb-8">For, {companyName}</p>
                <div className="border-t border-black pt-1 w-40">
                  <p className="text-center">Authorized Signatory</p>
                </div>
              </div>
            </div>

            {/* Footer — identical to AIR CAN */}
            <div id="sea-can-footer" className="mt-8">
              <div className="border-t border-black mb-2"></div>
              <div className="text-center text-[9px] text-gray-700">
                <p className="font-semibold">{companyName}</p>
                {companyAddress && <p>{companyAddress}</p>}
                <p>
                  {companyPhone && <span>Phone: {companyPhone}</span>}
                  {companyPhone && companyEmail && <span> | </span>}
                  {companyEmail && <span>Email: {companyEmail}</span>}
                </p>
                <p>
                  {companyGst && <span>GSTIN: {companyGst}</span>}
                  {companyGst && companyCin && <span> | </span>}
                  {companyCin && <span>CIN/LLP: {companyCin}</span>}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Print styles — mirror AIR CAN exactly */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }

          body * { visibility: hidden; }

          #sea-can-print-content,
          #sea-can-print-content * { visibility: visible; }

          #sea-can-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            min-height: calc(297mm - 20mm);
            display: flex !important;
            flex-direction: column !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          #sea-can-print-content > div {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #sea-can-print-content > div > div.relative {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #sea-can-footer { margin-top: auto !important; }

          table, td, th { border-color: black !important; }
        }

        @media screen {
          #sea-can-print-content {
            max-width: 210mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </>
  );
});

SeaCargoArrivalNoticePrint.displayName = 'SeaCargoArrivalNoticePrint';
export default SeaCargoArrivalNoticePrint;
